import { useEffect, useMemo, useRef, useState } from 'react';
import * as Location from 'expo-location';
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { useAppDispatch, useAppSelector } from '../hooks/store-hook';
import { userPrefsActions } from '../store/userPrefsSlice';
import { MysteryShopperAddressDto, MysteryShopperAddressType, MysteryShopperClient } from '../api/api.g';
import useApi from '../hooks/api-hook';
import { withTimeout } from '../helpers/withTimeout';
import useUserPrefsService from '../hooks/userPrefsService-hook';
import { MapLocation } from '../models/MapModels';
import { readCampaignLanding } from '../helpers/campaignEntry';

export enum LocationPermissionStatus {
  Granted,
  Denied,
  Undetermined,
}

const hasFiniteCoordinates = (location?: MapLocation) => Number.isFinite(location?.lat) && Number.isFinite(location?.lng);
const DEFAULT_LOCATION_FETCH_TIMEOUT_MS = 30000;

export interface UseMapLocationResult {
  fallBackLocation: MapLocation;
  serviceLocation: MapLocation | undefined;
  effectiveLocation: MapLocation | undefined;
  hasResolvedUserPosition: boolean;
  currentLocation: MapLocation | undefined;
  userLocation: MapLocation | undefined;
  askLocation: boolean | undefined;
  setAskLocation: (value: boolean | undefined) => void;
  locationPermissionGranted: boolean;
  locationPermissionStatus: LocationPermissionStatus | undefined;
  locationPermissionCanAskAgain: boolean | undefined;
  waitingForBrowserLocation: boolean;
  gpsFallbackToast: boolean;
  setGpsFallbackToast: (value: boolean) => void;
  dismissLocation: boolean;
  setDismissLocation: (value: boolean) => void;
  showedLocationPopup: boolean;
  setShowedLocationPopup: (value: boolean) => void;
  hasGpsFix: boolean;
  locationReady: boolean;
  triggerGetUserLocation: boolean;
  setTriggerGetUserLocation: (value: boolean) => void;
  getUserCurrentLocation: (skipGetMissions: boolean, overridePreselectedBranch: boolean, showLocationPermissionPopup: () => void) => void;
  setLocationPermissionState: (status: LocationPermissionStatus | undefined, granted: boolean, canAskAgain?: boolean) => void;
  useDefaultLocation: () => void;
  useServiceOrDefaultLocation: () => void;
}

export const useMapLocation = (): UseMapLocationResult => {
  const layoutFallBackLocation: MapLocation = (Constants.expoConfig as any).extra.defautlLocation;
  const dispatch = useAppDispatch();
  const userPrefs = useAppSelector((selector) => selector.userPrefs);
  const campaignUser = useAppSelector((selector) => selector.auth.user);
  const campaignLocation = useMemo<MapLocation | undefined>(() => {
    const landing = readCampaignLanding();
    return landing?.uid === campaignUser?.uid && Number.isFinite(landing?.lat) && Number.isFinite(landing?.lng)
      ? { lat: landing!.lat!, lng: landing!.lng!, isDefault: false } : undefined;
  }, [campaignUser?.uid]);
  const apiMysteryShopper = useApi(MysteryShopperClient);
  const userService = useUserPrefsService();

  const [currentLocation, setCurrentLocation] = useState<MapLocation | undefined>(undefined);
  const [userLocation, setUserLocation] = useState<MapLocation | undefined>(undefined);
  const [askLocation, setAskLocation] = useState<boolean | undefined>(false);
  const [locationPermissionGranted, setLocationPermissionGranted] = useState(false);
  const [waitingForBrowserLocation, setWaitingForBrowserLocation] = useState(false);
  const [gpsFallbackToast, setGpsFallbackToast] = useState(false);
  const [locationPermissionStatus, setLocationPermissionStatus] = useState<LocationPermissionStatus | undefined>(undefined);
  const [locationPermissionCanAskAgain, setLocationPermissionCanAskAgain] = useState<boolean | undefined>(undefined);
  const [dismissLocation, setDismissLocation] = useState(false);
  const [showedLocationPopup, setShowedLocationPopup] = useState(false);
  const [locationReady, setLocationReady] = useState(false);
  const [hasGpsFix, setHasGpsFix] = useState(false);
  const [triggerGetUserLocation, setTriggerGetUserLocation] = useState(false);
  const syncedCurrentAddressKeyRef = useRef<string | undefined>(undefined);

  const fallBackLocation = layoutFallBackLocation;

  const setLocationPermissionState = (status: LocationPermissionStatus | undefined, granted: boolean, canAskAgain?: boolean) => {
    setLocationPermissionStatus(status);
    setLocationPermissionGranted(granted);
    setLocationPermissionCanAskAgain(canAskAgain);
  };

  const serviceLocation = useMemo<MapLocation | undefined>(() => {
    const firstServiceAddress = (userPrefs.addresses ?? []).find((x) => x.type !== MysteryShopperAddressType.Current && x.geocoordinate);
    if (!firstServiceAddress?.geocoordinate) {
      return undefined;
    }
    const lat = Number(firstServiceAddress.geocoordinate.y);
    const lng = Number(firstServiceAddress.geocoordinate.x);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return undefined;
    }
    return { lat, lng };
  }, [userPrefs.addresses]);

  const effectiveLocation = useMemo<MapLocation | undefined>(() => {
    if (hasGpsFix && userLocation) {
      return userLocation;
    }
    if (serviceLocation) {
      return serviceLocation;
    }
    if (campaignLocation) {
      return campaignLocation;
    }
    if (userLocation) {
      return userLocation;
    }
    return undefined;
  }, [hasGpsFix, userLocation, serviceLocation, campaignLocation]);

  const hasResolvedUserPosition = useMemo(() => {
    return hasFiniteCoordinates(effectiveLocation);
  }, [effectiveLocation]);

  const setOpenMapAsBoundaries = (expansionAmount: number, position: MapLocation) => {
    // This helper was originally used to seed map bounds. It is kept here for
    // compatibility with the original behavior, but only updates local
    // location-related state. Map bounds are still controlled from CustomMap.
    setCurrentLocation(position);
    setUserLocation(position);
  };

  const buildServiceOrDefaultLocation = (): MapLocation => {
    if (serviceLocation) {
      return { lat: serviceLocation.lat, lng: serviceLocation.lng, isDefault: false };
    }

    return { lat: fallBackLocation.lat, lng: fallBackLocation.lng, isDefault: true };
  };

  const getLocationKey = (location?: MapLocation): string | undefined => {
    if (!hasFiniteCoordinates(location)) {
      return undefined;
    }

    return `${Number(location.lat).toFixed(5)}:${Number(location.lng).toFixed(5)}`;
  };

  const useServiceOrDefaultLocation = () => {
    const loc = buildServiceOrDefaultLocation();
    setCurrentLocation(loc);
    setUserLocation(loc);
    setOpenMapAsBoundaries(3, loc);
    setWaitingForBrowserLocation(false);
    setAskLocation(false);
    setHasGpsFix(!!serviceLocation);
    setLocationReady(true);
  };

  const useDefaultLocation = () => {
    useServiceOrDefaultLocation();
  };

  const handleMobileLocation = (location: Location.LocationObject, skipGetMissions: boolean, overridePreselectedBranch: boolean) => {
    if (location) {
      if (!skipGetMissions) {
        setCurrentLocation({ lat: location.coords.latitude, lng: location.coords.longitude, isDefault: false });
        if (!overridePreselectedBranch) {
          setOpenMapAsBoundaries(0.3, { lat: location.coords.latitude, lng: location.coords.longitude, isDefault: false });
        }
      }
      setUserLocation({ lat: location.coords.latitude, lng: location.coords.longitude, isDefault: false });
      if (!overridePreselectedBranch) {
        setOpenMapAsBoundaries(0.15, { lat: location.coords.latitude, lng: location.coords.longitude, isDefault: false });
      }
      setWaitingForBrowserLocation(false);
      setLocationPermissionState(LocationPermissionStatus.Granted, true, true);
      setLocationReady(true);
      setHasGpsFix(true);
      setAskLocation(false);
    }
  };

  const getMobileLastKnownLocation = async (skipGetMissions: boolean, overridePreselectedBranch: boolean) => {
    try {
      const lastKnown = await withTimeout(Location.getLastKnownPositionAsync({ maxAge: 300000 }), 3000);
      if (lastKnown) {
        handleMobileLocation(lastKnown, skipGetMissions, overridePreselectedBranch);
        return;
      }

      const current = await withTimeout(
        Location.getCurrentPositionAsync({
          accuracy: Location.Accuracy.High,
          mayShowUserSettingsDialog: true,
        }),
        DEFAULT_LOCATION_FETCH_TIMEOUT_MS,
      );
      handleMobileLocation(current, skipGetMissions, overridePreselectedBranch);
      return;
    } catch {
      try {
        const balancedCurrent = await withTimeout(
          Location.getCurrentPositionAsync({
            accuracy: Location.Accuracy.Balanced,
            mayShowUserSettingsDialog: true,
          }),
          15000,
        );
        handleMobileLocation(balancedCurrent, skipGetMissions, overridePreselectedBranch);
        return;
      } catch {
        try {
          const staleLastKnown = await withTimeout(Location.getLastKnownPositionAsync(), 3000);
          if (staleLastKnown) {
            handleMobileLocation(staleLastKnown, skipGetMissions, overridePreselectedBranch);
            setGpsFallbackToast(true);
            return;
          }
        } catch {
          // Keep fallback behavior below.
        }
      }

      setWaitingForBrowserLocation(false);
      setHasGpsFix(false);
      setGpsFallbackToast(true);
      useServiceOrDefaultLocation();
    }
  };

  const getUserCurrentLocation = (
    skipGetMissions: boolean,
    overridePreselectedBranch: boolean,
    showLocationPermissionPopup: () => void,
  ) => {
    setUserLocation(undefined);
    setWaitingForBrowserLocation(true);

    if (Platform.OS === 'web') {
      navigator.geolocation.getCurrentPosition(
        (lastKnownLocation) => {
          if (!skipGetMissions) {
            setCurrentLocation({ lat: lastKnownLocation.coords.latitude, lng: lastKnownLocation.coords.longitude, isDefault: false });
          }
          setUserLocation({ lat: lastKnownLocation.coords.latitude, lng: lastKnownLocation.coords.longitude, isDefault: false });
          setWaitingForBrowserLocation(false);
          setLocationPermissionState(LocationPermissionStatus.Granted, true, true);
          setLocationReady(true);
          setHasGpsFix(true);
          setAskLocation(false);
        },
        () => {
          setLocationPermissionState(LocationPermissionStatus.Denied, false);
          useServiceOrDefaultLocation();
        },
        {
          enableHighAccuracy: true,
          maximumAge: 300000,
          timeout: DEFAULT_LOCATION_FETCH_TIMEOUT_MS,
        },
      );
    } else {
      withTimeout(Location.getForegroundPermissionsAsync(), 3000).then((result) => {
        if (result.granted) {
          setLocationPermissionState(LocationPermissionStatus.Granted, true, result.canAskAgain);
          getMobileLastKnownLocation(skipGetMissions, overridePreselectedBranch);
          return;
        }

        if (result.status === 'undetermined') {
          setLocationPermissionState(LocationPermissionStatus.Undetermined, false, result.canAskAgain);
          if (result.canAskAgain) {
            setWaitingForBrowserLocation(false);
            showLocationPermissionPopup();
          } else {
            setWaitingForBrowserLocation(false);
            useDefaultLocation();
          }
          return;
        }

        if (result.status === 'denied') {
          setLocationPermissionState(LocationPermissionStatus.Denied, false, result.canAskAgain);
          const shouldShowPermissionPopup = !showedLocationPopup && result.canAskAgain;
          if (shouldShowPermissionPopup) {
            setWaitingForBrowserLocation(false);
            showLocationPermissionPopup();
          } else {
            setWaitingForBrowserLocation(false);
            useDefaultLocation();
          }
        }
      }).catch(() => {
        setGpsFallbackToast(true);
        useServiceOrDefaultLocation();
      });
    }
  };

  // Profile/location update should only run when the actual currentLocation changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!currentLocation) {
      return;
    }

    dispatch(userPrefsActions.setLocation(currentLocation));

    const defaultLoc = serviceLocation ?? fallBackLocation;
    if (currentLocation.lat === defaultLoc.lat && currentLocation.lng === defaultLoc.lng) {
      return;
    }

    // Avoid re-posting the same coordinates when location effects re-fire.
    const currentLocationKey = getLocationKey(currentLocation);
    if (currentLocationKey && syncedCurrentAddressKeyRef.current === currentLocationKey) {
      return;
    }
    syncedCurrentAddressKeyRef.current = currentLocationKey;

    const dto = new MysteryShopperAddressDto();
    dto.latitude = currentLocation.lat;
    dto.longitude = currentLocation.lng;
    dto.type = MysteryShopperAddressType.Current;
    apiMysteryShopper
      .updateCurrentAddress(dto, apiMysteryShopper.tokenSource?.token)
      .then(() => {
        userService.updateUserProfileInfo();
      })
      .catch(() => {
        if (syncedCurrentAddressKeyRef.current === currentLocationKey) {
          syncedCurrentAddressKeyRef.current = undefined;
        }
        // Keep original behavior: log error only in CustomMap; no-op here.
      });
  }, [currentLocation]);

  useEffect(() => {
    if (hasResolvedUserPosition && hasFiniteCoordinates(effectiveLocation)) {
      setLocationReady(true);
    }
  }, [hasResolvedUserPosition, effectiveLocation]);

  useEffect(() => {
    const shouldPromoteServiceLocation =
      !!serviceLocation &&
      !!currentLocation?.isDefault &&
      !hasGpsFix &&
      !locationPermissionGranted &&
      !waitingForBrowserLocation;

    if (!shouldPromoteServiceLocation) {
      return;
    }

    const currentLocationKey = getLocationKey(currentLocation);
    const serviceLocationKey = getLocationKey(serviceLocation);

    if (!serviceLocationKey || currentLocationKey === serviceLocationKey) {
      return;
    }

    const promotedServiceLocation: MapLocation = {
      lat: serviceLocation.lat,
      lng: serviceLocation.lng,
      isDefault: false,
    };

    setCurrentLocation(promotedServiceLocation);
    setUserLocation(promotedServiceLocation);
    setHasGpsFix(true);
    setLocationReady(true);
    setWaitingForBrowserLocation(false);
  }, [serviceLocation, currentLocation, hasGpsFix, locationPermissionGranted, waitingForBrowserLocation]);

  useEffect(() => {
    if (!gpsFallbackToast) {
      return;
    }
    const timer = setTimeout(() => {
      setGpsFallbackToast(false);
    }, 3500);
    return () => clearTimeout(timer);
  }, [gpsFallbackToast]);


  return {
    fallBackLocation,
    serviceLocation,
    effectiveLocation,
    hasResolvedUserPosition,
    currentLocation,
    userLocation,
    askLocation,
    setAskLocation,
    locationPermissionGranted,
    locationPermissionStatus,
    locationPermissionCanAskAgain,
    waitingForBrowserLocation,
    gpsFallbackToast,
    setGpsFallbackToast,
    dismissLocation,
    setDismissLocation,
    showedLocationPopup,
    setShowedLocationPopup,
    hasGpsFix,
    locationReady,
    triggerGetUserLocation,
    setTriggerGetUserLocation,
    getUserCurrentLocation,
    setLocationPermissionState,
    useDefaultLocation,
    useServiceOrDefaultLocation,
  };
};

export default useMapLocation;
